<template>
  <!-- Currently Serving Eligibility Case -->
  <div id="currently-serving">
    <div class="form-section">
      <VeteranInfo
        v-if="formResponse && formResponse.veteranInfo"
        :subFormData="formResponse.veteranInfo"
        :referenceData="referenceData"
        :serviceAgencyLabel="formLabels.veteranInfo.serviceAgencyLabel"
        :serviceTypeLabel="formLabels.veteranInfo.serviceTypeLabel"
        :noEligibilityChange="noEligibilityChange"
        :disableEligibilityFields="disableEligibilityFields"
        :initialData="initialData"
        :serviceConnection="serviceConnection"
        :defaultVeteranInfo="defaultSubFormTemplate.veteranInfo"
        :isChangeCover="isChangeCover"
        @updated="updateVeteranInfo"
      />
    </div>

    <!-- Date of Enlistment Input Field -->
    <div class="form-section" :key="`enlistment-date${disableEligibilityFields ? '-disable' : ''}`">
      <template v-if="disableEligibilityFields">
        <FormField id="enlistment-date" labelText="Date of enlistment">
          <p class="disabled-input date-input">{{ formResponse.enlistmentDate }}</p>
        </FormField>
      </template>
      <template v-else>
        <FormField
          id="enlistment-date"
          class="date-input"
          labelText="Date of enlistment"
          placeholder="DD / MM / YYYY"
          v-maska="'## / ## / ####'"
          type="text"
          :isDateInput="true"
          maxlength="14"
          v-model.lazy.trim="v$.formResponse.enlistmentDate.$model"
          :error="v$.formResponse.enlistmentDate.$error"
          :errorMessage="dateOfEnlistmentError()"
        />
      </template>
    </div>

    <!-- Sercat Status Dropdown -->
    <div class="form-section">
      <FormField
        :labelText="referenceData.sercat.label"
        forId="sercat-dropdown"
        :placeholder="referenceData.sercat.options[0].display"
        id="sercat-status"
        :error="v$.formResponse.sercatStatus.$dirty && v$.formResponse.sercatStatus.$error"
        :errorMessage="'This information is required. Please make a selection.'"
      >
        <DropdownSelect
          initialSelect="Please select a SERCAT status"
          :items="getDropdownOptions(referenceData.sercat.options)"
          v-model="v$.formResponse.sercatStatus.$model"
          :disabled="disableEligibilityFields"
          id="sercat-dropdown"
        />
      </FormField>
    </div>

    <!-- Sercat File Upload -->
    <div class="form-section" v-if="!disableEligibilityFields">
      <FormField
        labelText="Supporting documents"
        forId="sercat-file-upload"
        :required="fileRequired"
        :helperText="referenceData.sercatHelpText ? referenceData.sercatHelpText.informationText : null"
      >
        <p>You are required to hold valid evidence in order to maintain your eligibility to this health insurance product. We may reach out and request a copy.</p>
        <!-- <FileSelect
          id="sercat-file-upload"
          name="sercat-file-upload"
          type="file"
          :uploadedFiles="uploadedFiles"
          :uploadStatus="uploadStatus"
          :errorMessage="uploadErrorMessage"
          @upload="fileUploadFn"
          @removeError="uploadStatus = ''"
          :removedFilesIndexList="removedFilesIndexList"
          ref="file-upload"
        /> -->
      </FormField>
    </div>
  </div>
</template>

<script>
import { required, minLength, requiredIf } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { DATE_VALIDATOR } from '@general/js/validators';
import { FormField } from '@/components';
import { Eligibility, LoadingStatus } from '@general/js/enums';
import { mapMutations } from 'vuex';
import { getServiceAgencyLabel, getServicePeriodLabel, isInvalid, lessThan90Days, overPastDate } from '@general/js/utils';
import JoinNowServices from '@general/js/services/JoinNow';
import DropdownSelect from '@general/js/components/DropdownSelect.vue';
// import { FileSelect, VeteranInfo } from './components';
import { VeteranInfo } from './components';

export default {
  setup() { return { v$: useVuelidate() }; },
  name: 'CurrentlyServing',

  data() {
    return {
      formLabels: {
        veteranInfo: {
          serviceAgencyLabel: '',
          serviceTypeLabel: '',
        },
      },
      formResponse: {
        enlistmentDate: '',
        sercatStatus: '',
        sercatFileNames: [],
      },
      eligibility: Eligibility.SERVING,
      uploadedFiles: {
        fileNames: [],
        fileIndices: [],
        size: 0,
        fileInvalid: false,
      },
      uploadStatus: '',
      uploadErrorMessage: '',
      uploadErrorFiles: [],
      veteranInfoIsInvalid: true,
      removedFilesIndexList: [],
    };
  },
  components: {
    FormField,
    // FileSelect,
    VeteranInfo,
    DropdownSelect,
  },

  props: {
    subFormData: {
      type: Object,
      required: true,
    },
    referenceData: {
      type: Object,
      required: true,
    },
    contactId: {
      type: String,
      required: true,
    },
    fileRequired: {
      type: Boolean,
      default: false,
    },
    noEligibilityChange: {
      type: Boolean,
    },
    disableEligibilityFields: {
      type: Boolean,
    },
    initialData: {
      type: Object,
    },
    serviceConnection: {
      type: String,
    },
    defaultSubFormTemplate: {
      type: Object,
    },
    isChangeCover: {
      type: Boolean,
    },
  },

  watch: {
    formResponse: {
      handler() {
        if (this.formResponse) {
          this.$emit('updated', { subFormData: this.formResponse, isInvalid: this.v$.$invalid || this.v$.$error });
        }
      },
      deep: true,
    },
    // Clears and restores prefill data when service connection changes (Self, Partner)
    serviceConnection(newValue) {
      if (this.isChangeCover && this.noEligibilityChange && this.initialData.serviceConnection !== '') {
        if (newValue !== this.initialData.serviceConnection) {
          this.formResponse = JSON.parse(JSON.stringify(this.defaultSubFormTemplate));
        } else {
          this.formResponse = JSON.parse(JSON.stringify(this.initialData.subFormData));
        }
      }
    },
  },

  mounted() {
    this.formLabels.veteranInfo.serviceAgencyLabel = getServiceAgencyLabel(this.eligibility);
    this.formLabels.veteranInfo.serviceTypeLabel = getServicePeriodLabel(this.eligibility);
    this.formResponse = JSON.parse(JSON.stringify(this.subFormData));

    // When the sub form data arrives, check to determine if there are uploaded files
    if (this.formResponse.sercatFileNames && this.formResponse.sercatFileNames.length > 0) {
      this.uploadedFiles.fileNames = [...this.formResponse.sercatFileNames];
    }
  },

  validations: {
    formResponse: {
      enlistmentDate: {
        required,
        DATE_VALIDATOR,
        overPastDate,
        lessThan90Days,
      },
      sercatStatus: {
        required,
      },
      sercatFileNames: {
        minLength: minLength(1),
        required: requiredIf(function () {
          return this.fileRequired;
        }),
      },
    },
    veteranInfoIsInvalid: {
      isInvalid,
    },
  },

  methods: {
    ...mapMutations(['updateEligibilityFormData']),

    // upload Files for JOL
    async uploadFiles(files) {
      const formData = new FormData();
      this.uploadStatus = LoadingStatus.PENDING;
      if (files) {
        Array.from(files).forEach((file) => {
          formData.append('file', file, file.name);
        });
      }
      try {
        const res = await JoinNowServices.uploadDocument(formData, this.contactId, 'sercat');
        if (res === LoadingStatus.SUCCESS) {
          this.uploadStatus = LoadingStatus.SUCCESS;
          this.uploadSuccess = true;
          Array.from(files).forEach((file) => {
            this.uploadedFiles.fileNames.push(file.name);
            this.formResponse.sercatFileNames.push(file.name);
            this.uploadedFiles.size += file.size;
          });
        }
      } catch (e) {
        this.uploadStatus = LoadingStatus.ERROR;
        this.uploadErrorMessage = e.message;
      }
    },

    // Upload Files for change cover
    async changeCoverUploadFiles(files) {
      this.uploadStatus = LoadingStatus.PENDING;
      this.uploadedFiles.size = 0;
      let isAllSuccess = true;
      await Promise.allSettled(
        files.map(async (file, index) => {
          const base64Str = await this.convertToBase64(file);
          const payload = {
            fileInput: base64Str ? base64Str.split(',')[1] : '',
            fileName: file.name,
            fileType: file.type,
            identifier: this.contactId,
            category: 'SERCAT',
          };
          try {
            await JoinNowServices.uploadFile(payload); // return "success" if file uploaded successfully
            this.uploadedFiles.fileNames.push(file.name);
            this.uploadedFiles.fileIndices.push(index);
            this.formResponse.sercatFileNames.push(file.name);
            this.removedFilesIndexList.push(files.indexOf(file));
            files.splice(files.indexOf(file), 1); // removed this item from loading file array

            this.uploadedFiles.size += file.size;
          } catch (error) {
            isAllSuccess = isAllSuccess && false;
            this.uploadStatus = LoadingStatus.ERROR;
            this.uploadErrorMessage = error.message;
          }
        }),
      );
      // return true if all requests have been resolved
      if (isAllSuccess) {
        this.uploadStatus = LoadingStatus.SUCCESS;
      } else {
        this.uploadStatus = LoadingStatus.ERROR;
      }
    },

    updateVeteranInfo(data) {
      this.formResponse.veteranInfo = JSON.parse(JSON.stringify(data.veteranInfo));
      this.veteranInfoIsInvalid = data.isInvalid;
    },

    dateOfEnlistmentError() {
      if (this.v$.formResponse.enlistmentDate.$error && this.v$.formResponse.enlistmentDate.DATE_VALIDATOR.$invalid) {
        return 'Please enter the enlistment date using the format DD/MM/YYYY.';
      }

      if (this.v$.formResponse.enlistmentDate.$error && this.v$.formResponse.enlistmentDate.overPastDate.$invalid) {
        return 'You must be 16 years and 6 months to have joined the army.';
      }

      if (this.v$.formResponse.enlistmentDate.$error && this.v$.formResponse.enlistmentDate.lessThan90Days.$invalid) {
        return 'We can only accept future dates within 90 days of today.';
      }

      return 'Please include a response for date of enlistment.';
    },

    convertToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          resolve(reader.result);
        };
        reader.onerror = (error) => {
          reject(error);
        };
      });
    },

    getDropdownOptions(options) {
      const dropdownOptions = options.map(({ display, returnValue }) => {
        return {
          name: display,
          value: returnValue,
        };
      });

      return dropdownOptions;
    },
  },

  computed: {
    calcSercatStatusDisplayValue() {
      return this.referenceData.sercat.options.filter((option) => {
        if (this.formResponse.sercatStatus !== undefined) {
          return option.returnValue === this.formResponse.sercatStatus || option.display === this.formResponse.sercatStatus;
        }
        return null;
      })[0]?.display;
    },

    // Switch file upload function between ChangeCover and JOL
    fileUploadFn() {
      return this.isChangeCover ? this.changeCoverUploadFiles : this.uploadFiles;
    },
  },
};
</script>

<style lang="scss" scoped>
#currently-serving {
  .date-input {
    width: 50%;
  }
}

select[disabled]:not([disabled='false']) + .dropdown-face {
  .dropdown-face-text {
    color: $DISABLED_BTN_TEXT_COLOR;
  }

  &:read-only::before {
    border-bottom-color: $DISABLED_BTN_TEXT_COLOR;
  }

  &:read-only::after {
    border-top-color: $DISABLED_BTN_TEXT_COLOR;
  }
}
</style>
